import { AddIcon, LoadingIcon } from '@assets/icons';
import { Button, TableView, TableViewButton } from '@components/button';
import { SearchAndFilter } from '@components/filter';
import { useGraphqlPagination } from '@components/infiniteScroll';
import { Group } from '@components/layout';
import { useModal } from '@components/modal';
import { Typography } from '@components/typography';
import {
  GetGraphsQuery,
  Graph,
  GraphFilter,
  useGetGraphsSearchDetailQuery,
  useGetGraphsWithFiltersQuery,
} from '@generated/graphql';
import { useSearchDetails } from '@hooks/SearchDetails';
import { useQueryParam } from '@hooks/UseQueryParam';
import { Stack } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { useWorkspaceContext } from '@navigation/WorkspaceContext';
import { navigate } from 'gatsby';
import { useCallback } from 'react';
import { GraphsAddGraphModal } from './GraphsAddGraphModal';
import { GraphsGridView } from './GraphsGridView';
import { GraphsListView } from './GraphsListView';

export const GraphsWorkspaceGraphs = () => {
  const { organizationId } = useOrganizationContext();
  const { workspaceId } = useWorkspaceContext();
  const { activeFilters, filter, queryFilter, setFilter } = useSearchDetails<GraphFilter>({
    channels: { type: 'exact', value: [], display: [] },
    dates: { type: 'date', value: [], display: [] },
    tags: { type: 'string', value: [], display: [] },
    texts: { type: 'string', value: [], display: [] },
    users: { type: 'exact', value: [], display: [] },
  });

  const { data, refetch: refetchSearchDetail } = useGetGraphsSearchDetailQuery({
    variables: { workspaceId, staged: false },
  });
  const searchDetail = data?.getGraphsSearchDetail;

  const pagination = useGraphqlPagination<GetGraphsQuery, Graph>(
    useGetGraphsWithFiltersQuery({
      variables: { limit: 75, staged: false, workspaceId, filters: queryFilter },
    }),
    'getGraphs',
    'graphId',
  );

  const refetch = () => {
    pagination.refetch();
    void refetchSearchDetail();
  };

  const { value: tableView, setValue: setTableView } = useQueryParam('tableView', 'grid');

  const isItemInListView = tableView === 'list';

  const onItemClick = useCallback(
    (graphId: string) => {
      void navigate(`/${organizationId}/workspaces/${workspaceId}/graphs/${graphId}`);
    },
    [organizationId, workspaceId],
  );

  const { showModal } = useModal({
    component: GraphsAddGraphModal,
    componentProps: { workspaceId },
    modalProps: { title: 'New Graph' },
  });

  return (
    <Stack gap={3} flex={1}>
      <Group justifyContent="space-between" alignItems="flex-start" gap={4}>
        <SearchAndFilter
          context="workspace-graphs"
          filter={filter}
          setFilter={setFilter}
          searchDetail={searchDetail}
        />
        <TableViewButton mode={tableView as TableView} onChange={setTableView} />
        <Button startIcon={AddIcon} onClick={() => showModal()}>
          New graph
        </Button>
      </Group>
      {!pagination?.loading ? (
        pagination?.data?.length > 0 ? (
          isItemInListView ? (
            <GraphsListView pagination={pagination} refetch={refetch} onItemClick={onItemClick} />
          ) : (
            <GraphsGridView pagination={pagination} refetch={refetch} onItemClick={onItemClick} />
          )
        ) : (
          <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
            <Typography>
              {activeFilters
                ? 'No graphs found matching your search criteria.'
                : 'You have no graphs.'}
            </Typography>
          </Stack>
        )
      ) : (
        <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
          <LoadingIcon size={16} />
        </Stack>
      )}
    </Stack>
  );
};
